import { fetchMFAPreference } from '@aws-amplify/auth';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { INACTIVITY_LOCK_PIN_LENGTH } from '@chiroup/core/constants/inactivityLock';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Delete as BackspaceIcon, Lock as LockClosedIcon } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import userService from '../../../services/user.service';

type Props = {
  open: boolean;
  hasPinConfigured: boolean;
  userEmail: string;
  /**
   * Called after a successful unlock. May return a promise — the overlay
   * keeps the unlock button in its busy state until it resolves so the user
   * can't double-submit while /me refetches the full payload.
   */
  onUnlock: () => void | Promise<void>;
  onSessionExpired: () => void;
};

type Method = 'password' | 'pin';

const InactivityLockOverlay: React.FC<Props> = ({
  open,
  hasPinConfigured,
  userEmail,
  onUnlock,
  onSessionExpired,
}) => {
  // Cognito dispatches SMS/EMAIL MFA codes as a side effect of AdminInitiateAuth
  // even when we never answer the challenge — warn the user so the surprise code
  // isn't mistaken for a sign-in attempt. Fetched from Amplify (Cognito is the
  // source of truth) rather than `me.mfa`, which is stale: the MFA modal goes
  // straight to Amplify and the User.mfa DB column never gets updated.
  const [mfaCodeChannel, setMfaCodeChannel] = useState<'SMS' | 'email' | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchMFAPreference()
      .then(pref => {
        if (cancelled) return;
        const enabled = pref.enabled ?? [];
        if (enabled.includes('SMS')) setMfaCodeChannel('SMS');
        else if (enabled.includes('EMAIL')) setMfaCodeChannel('email');
        else setMfaCodeChannel(null);
      })
      .catch(() => {
        // Fail closed — no warning if we can't tell. Worst case the user sees
        // a code they don't expect; better than a false warning when they have
        // no MFA configured at all.
        if (!cancelled) setMfaCodeChannel(null);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);
  const [method, setMethod] = useState<Method>(hasPinConfigured ? 'pin' : 'password');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  // Reset transient form state every time the overlay opens.
  useEffect(() => {
    if (!open) return;
    setMethod(hasPinConfigured ? 'pin' : 'password');
    setPassword('');
    setPin('');
    setError(null);
    setBusy(false);
  }, [open, hasPinConfigured]);

  // Focus the password input when in password mode. The PIN mode is keypad-
  // driven, so no input focus is needed.
  useEffect(() => {
    if (!open || method !== 'password') return;
    queueMicrotask(() => passwordInputRef.current?.focus());
  }, [open, method]);

  // Escape and outside-click handling is delegated to the Radix Dialog
  // wrapper below — it joins the DismissableLayer stack as the topmost layer,
  // which both blocks Escape from reaching underlying dialogs and stops their
  // outside-click handlers from firing when the user clicks our keypad.

  // Belt-and-suspenders for non-modal underlying dialogs (e.g. the schedule
  // appointment slideover uses Radix Sheet with `modal={false}`, which sits
  // outside Radix's layersWithOutsidePointerEventsDisabled mechanism and
  // closes on outside pointerdown despite our top-of-stack modal Dialog).
  //
  // We listen at window capture for native pointerdown/mousedown. When the
  // target is inside our overlay, we stopImmediatePropagation so document-
  // level outside-click detectors never see the event. `click` is a separate
  // event that still propagates normally — our PIN buttons keep working.
  const overlayNodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const swallowIfInside = (e: PointerEvent | MouseEvent) => {
      const target = e.target as Node | null;
      if (target && overlayNodeRef.current?.contains(target)) {
        e.stopImmediatePropagation();
      }
    };

    window.addEventListener('pointerdown', swallowIfInside, true);
    window.addEventListener('mousedown', swallowIfInside, true);

    return () => {
      window.removeEventListener('pointerdown', swallowIfInside, true);
      window.removeEventListener('mousedown', swallowIfInside, true);
    };
  }, [open]);

  // Hardware keyboard input for the PIN keypad — digit keys append, Backspace
  // removes. Auto-submits when the PIN reaches its required length, mirroring
  // the on-screen keypad.
  useEffect(() => {
    if (!open || !hasPinConfigured || method !== 'pin') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (busy) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        setPin(prev => {
          if (prev.length >= INACTIVITY_LOCK_PIN_LENGTH) return prev;
          const next = prev + e.key;
          if (next.length === INACTIVITY_LOCK_PIN_LENGTH) {
            void submitPinValue(next);
          }
          return next;
        });
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setError(null);
        setPin(prev => prev.slice(0, -1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, hasPinConfigured, method, busy]);

  if (!open) return null;

  const handleApiError = (err: unknown) => {
    const status = (err as { response?: { data?: { statusCode?: number } } })?.response?.data
      ?.statusCode;
    const message =
      (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
      'Unlock failed. Please try again.';

    // 401 with no attemptsRemaining means the Cognito JWT itself is invalid —
    // session has expired. Force re-login.
    if (status === 401) {
      const remaining = (err as { response?: { data?: { attemptsRemaining?: number } } }).response
        ?.data?.attemptsRemaining;

      if (remaining === undefined) {
        onSessionExpired();

        return;
      }
    }

    if (status === 429) {
      onSessionExpired();

      return;
    }

    setError(message);
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !password) return;
    setBusy(true);
    setError(null);

    try {
      await userService.sessionUnlock({ password });
      await onUnlock();
    } catch (err) {
      setPassword('');
      handleApiError(err);
    } finally {
      setBusy(false);
    }
  };

  // Auto-submits when 6 digits are entered via the keypad.
  const submitPinValue = async (value: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await userService.sessionPinUnlock({ pin: value });
      await onUnlock();
    } catch (err) {
      setPin('');
      handleApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const pressKey = (digit: string) => {
    if (busy || pin.length >= INACTIVITY_LOCK_PIN_LENGTH) return;
    const next = pin + digit;
    setPin(next);
    if (next.length === INACTIVITY_LOCK_PIN_LENGTH) {
      void submitPinValue(next);
    }
  };

  const pressBackspace = () => {
    if (busy) return;
    setPin(prev => prev.slice(0, -1));
    setError(null);
  };

  return (
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          ref={overlayNodeRef}
          // Used by `SlideOver` (and other dismissable-layer hosts) to recognize
          // our overlay and skip outside-click / focus-outside dismissal when
          // the user interacts with the unlock UI on top.
          data-component="inactivity-lock-overlay"
          aria-label="Session locked"
          // Keep our own focus logic in charge (password input ref / no-op for
          // the keypad). Radix would otherwise grab the first focusable button.
          onOpenAutoFocus={e => e.preventDefault()}
          // Escape and outside-click are non-actions for an inactivity lock.
          onEscapeKeyDown={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
          onPointerDownOutside={e => e.preventDefault()}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/80 backdrop-blur-md p-4"
        >
          <DialogPrimitive.Title className="sr-only">Session locked</DialogPrimitive.Title>
          <div className="w-full max-w-xs rounded-2xl bg-white dark:bg-dark-gray-900 shadow-2xl p-6 space-y-5">
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="h-12 w-12 rounded-full bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center">
                <LockClosedIcon className="h-6 w-6 text-primary-500" aria-hidden="true" />
              </div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Session locked
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-full">
                {userEmail}
              </p>
            </div>

            {hasPinConfigured && method === 'pin' ? (
              <>
                <div
                  className="flex justify-center gap-3"
                  aria-label={`PIN entry, ${pin.length} of ${INACTIVITY_LOCK_PIN_LENGTH} digits entered`}
                >
                  {Array.from({ length: INACTIVITY_LOCK_PIN_LENGTH }).map((_, i) => (
                    <span
                      key={i}
                      className={`h-3 w-3 rounded-full border ${
                        i < pin.length
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-gray-300 dark:border-dark-gray-700'
                      }`}
                    />
                  ))}
                </div>

                {error ? (
                  <p className="text-center text-sm text-red-600" role="alert">
                    {error}
                  </p>
                ) : null}

                <div className="grid grid-cols-3 gap-2">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(d => (
                    <button
                      key={d}
                      type="button"
                      disabled={busy}
                      onClick={() => pressKey(d)}
                      className="h-12 rounded-lg bg-gray-50 dark:bg-dark-gray-800 text-lg font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-dark-gray-700 disabled:opacity-50 transition"
                    >
                      {d}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setMethod('password');
                      setError(null);
                    }}
                    className="h-12 rounded-lg text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-dark-gray-800 transition"
                  >
                    Password
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => pressKey('0')}
                    className="h-12 rounded-lg bg-gray-50 dark:bg-dark-gray-800 text-lg font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-dark-gray-700 disabled:opacity-50 transition"
                  >
                    0
                  </button>
                  <button
                    type="button"
                    disabled={busy || pin.length === 0}
                    onClick={pressBackspace}
                    aria-label="Backspace"
                    className="h-12 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-gray-800 disabled:opacity-30 transition flex items-center justify-center"
                  >
                    <BackspaceIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </>
            ) : (
              <form onSubmit={submitPassword} className="space-y-3">
                <input
                  ref={passwordInputRef}
                  type="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={busy}
                  className="w-full rounded-lg border border-gray-300 dark:border-dark-gray-700 bg-white dark:bg-dark-gray-800 px-3 py-2 text-sm"
                />
                {mfaCodeChannel ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    You may receive an MFA verification{' '}
                    {mfaCodeChannel === 'SMS' ? 'text' : 'email'} when you unlock - you can ignore
                    it.
                    {!hasPinConfigured
                      ? ' To skip this next time, set up a PIN in your account settings.'
                      : null}
                  </p>
                ) : null}
                {error ? <p className="text-sm text-red-600">{error}</p> : null}
                <Button
                  type="submit"
                  text={busy ? 'Unlocking…' : 'Unlock'}
                  color={ButtonColors.primary}
                  disabled={busy || !password}
                  fullWidth
                />
                {hasPinConfigured ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMethod('pin');
                      setError(null);
                    }}
                    className="block w-full text-center text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Use PIN instead
                  </button>
                ) : null}
              </form>
            )}

            <button
              type="button"
              onClick={onSessionExpired}
              className="block w-full text-center text-xs text-gray-500 dark:text-gray-400 hover:underline"
            >
              Sign out instead
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default InactivityLockOverlay;
